import {
  CollapsibleGraphGroup,
  ComponentGraphGroup,
  GraphItemsModel,
  RelationshipDiagramViewSettings,
} from '@ardoq/graph';
import { ButtonConfig, updateViewSettings } from '@ardoq/view-settings';
import {
  collapseAll as collapseAllBase,
  expandAll as expandAllBase,
} from 'tabview/relationshipDiagrams/leftMenuItems';
import { type I18nLocale } from '@ardoq/i18n';
import { isEqual, noop } from 'lodash';
import { WILDCARD } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import { relationshipsViewSetCollapsedGroupIds } from './actions';
import { everyDescendant } from './util';
import { ViewIds } from '@ardoq/api-types';
import { RelationshipsNode } from './types';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

type ExpandAllButtonConfigParams = {
  viewSettings: RelationshipDiagramViewSettings;
  isDisabled: boolean;
  viewInstanceId: string;
  i18nLocale: I18nLocale;
};
type CollapseAllButtonConfigParams = ExpandAllButtonConfigParams & {
  rootNode: RelationshipsNode | null;
};

export const getCollapseAllButtonConfig = ({
  viewSettings,
  isDisabled,
  rootNode,
  viewInstanceId,
  i18nLocale,
}: CollapseAllButtonConfigParams): ButtonConfig => ({
  ...collapseAllBase(
    viewSettings,
    VIEW_ID,
    viewInstanceId,
    isDisabled,
    i18nLocale
  ),
  ...(isDisabled
    ? {
        onClick: noop,
        isActive: false,
      }
    : {
        onClick: () => {
          const newCollapsedGroupIds = [WILDCARD];
          if (!isEqual(viewSettings.collapsedGroupIds, newCollapsedGroupIds)) {
            notifyCollapsedGroupIdsChanged({
              collapsedGroupIds: newCollapsedGroupIds,
              viewInstanceId,
            });
          }
        },
        isActive:
          viewSettings.collapsedGroupIds.includes(WILDCARD) ||
          (rootNode !== null &&
            everyDescendant(
              rootNode,
              node => !node.descendantCount || !node.open
            )),
      }),
});

export const getExpandAllButtonConfig = ({
  viewSettings,
  isDisabled,
  viewInstanceId,
  i18nLocale,
}: ExpandAllButtonConfigParams): ButtonConfig => ({
  ...expandAllBase(
    viewSettings,
    VIEW_ID,
    viewInstanceId,
    isDisabled,
    i18nLocale
  ),
  onClick: !isDisabled
    ? () => {
        const newCollapsedGroupIds: string[] = [];
        if (!isEqual(viewSettings.collapsedGroupIds, newCollapsedGroupIds)) {
          notifyCollapsedGroupIdsChanged({
            collapsedGroupIds: newCollapsedGroupIds,
            viewInstanceId,
          });
        }
      }
    : noop,
});

type NotifyHighlightDisconnectedComponentsChangedArgs = {
  highlightDisconnectedComponents: boolean;
  persistent: boolean;
};
/**
 * Inverts the supplied highlightDisconnectedComponents boolean
 * and dispatches updateViewSettings action
 */
export const notifyHighlightDisconnectedComponentsChanged = ({
  highlightDisconnectedComponents,
  persistent,
}: NotifyHighlightDisconnectedComponentsChangedArgs) => {
  const newHighlightDisconnectedComponents = !highlightDisconnectedComponents;
  dispatchAction(
    updateViewSettings({
      viewId: VIEW_ID,
      settings: {
        highlightDisconnectedComponents: newHighlightDisconnectedComponents,
      },
      persistent,
    })
  );
};

type NotifyCollapsedGroupIdsChangedArgs = {
  collapsedGroupIds: string[];
  viewInstanceId: string;
};
const notifyCollapsedGroupIdsChanged = ({
  collapsedGroupIds,
  viewInstanceId,
}: NotifyCollapsedGroupIdsChangedArgs) => {
  dispatchAction(
    relationshipsViewSetCollapsedGroupIds({
      collapsedGroupIds,
    }),
    viewInstanceId
  );
  dispatchAction(
    updateViewSettings({
      viewId: VIEW_ID,
      settings: { collapsedGroupIds },
      persistent: true,
    })
  );
};

export const bundleRelationshipsHandler = (newValue: boolean) => () => {
  dispatchAction(
    updateViewSettings({
      viewId: ViewIds.RELATIONSHIPS_3,
      settings: { bundleRelationships: newValue },
      persistent: true,
    })
  );
};

export const toActiveGroups = (
  groups: Pick<
    GraphItemsModel<CollapsibleGraphGroup | ComponentGraphGroup>,
    'add' | 'update'
  >
): string[] => [...groups.add, ...groups.update];
